import { useState } from 'react'
import { useParams, navigate } from '@cedarjs/router'
import { useQuery, useMutation } from '@cedarjs/web'
import { gql } from '@apollo/client'
import { Typography, CircularProgress, Alert, Box, Button } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import PrintIcon from '@mui/icons-material/Print'
import { routes } from '@cedarjs/router'
import { Metadata } from '@cedarjs/web'
import { toast } from 'react-hot-toast'
import { useBreadcrumbs } from 'src/layouts/components/BreadcrumbsContext'
import RootPageStyledDiv from 'src/layouts/components/RootPageStyledDiv'
import IsolatedFormRenderer from 'src/components/IsolatedFormRenderer/IsolatedFormRenderer'
import ConfirmationModal from 'src/components/ConfirmationModal/ConfirmationModal'
import { useDispatch } from 'src/hooks/useDispatch'
import { openModal } from 'src/slices/modal'
import printForm from 'src/utils/printForm'

const FORM_SUBMISSION_QUERY = gql`
  query FormSubmission($id: String!) {
    formSubmission(id: $id) {
      id
      formTemplateId
      formTemplate {
        id
        title
        description
        formSchema
      }
      submissionData
      status
      submissionType
      submittedAt
      archivedAt
      submittingUser {
        id
        firstName
        lastName
        email
      }
      submissionFileId
      submissionFile {
        id
        url
        urlForPdfPreview
        originalFilename
        title
      }
    }
  }
`

const DELETE_FORM_SUBMISSION_MUTATION = gql`
  mutation DeleteFormSubmission($id: String!) {
    deleteFormSubmission(id: $id) {
      id
    }
  }
`

type ViewFormSubmissionPageProps = {
  orgName: string
}

const ViewFormSubmissionPage = ({ orgName }: ViewFormSubmissionPageProps) => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const [isDeleting, setIsDeleting] = useState(false)

  useBreadcrumbs([
    { icon: 'forms' },
    { label: 'Forms', to: routes.forms({ orgName }) },
    { label: 'View Submission' },
  ])

  const { data, loading, error } = useQuery(FORM_SUBMISSION_QUERY, {
    variables: { id },
    skip: !id,
  })

  const submission = data?.formSubmission
  const isPdfSubmission = submission?.submissionType === 'PDF'

  const [deleteFormSubmission] = useMutation(DELETE_FORM_SUBMISSION_MUTATION)

  const handleDeleteClick = () => {
    dispatch(
      openModal({
        name: 'confirmationModal',
        data: {
          discriminator: 'delete-submission',
        },
      })
    )
  }

  const handleDeleteConfirm = async () => {
    if (!id) return

    setIsDeleting(true)
    try {
      await deleteFormSubmission({
        variables: { id },
      })
      toast.success('Form submission deleted successfully')
      navigate(routes.forms({ orgName }))
    } catch (error) {
      console.error('Error deleting form submission:', error)
      toast.error('Failed to delete form submission')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteDeny = () => {
    // Modal will close automatically
  }

  const getPageTitle = () => {
    if (submission?.formTemplate?.title) {
      return `View Submission - ${submission.formTemplate.title}`
    }
    return 'View Form Submission'
  }

  const renderContent = () => {
    // Loading state
    if (loading) {
      return (
        <Box className="flex items-center justify-center py-8">
          <CircularProgress />
          <Typography variant="body2" className="ml-2">
            Loading form submission...
          </Typography>
        </Box>
      )
    }

    // Error state
    if (error) {
      console.error('Error loading form submission:', error)
      return (
        <Box className="py-8">
          <Alert severity="error">
            Failed to load form submission. Please try again.
          </Alert>
        </Box>
      )
    }

    // No submission found
    if (!submission) {
      return (
        <Box className="py-8">
          <Alert severity="warning">Form submission not found.</Alert>
        </Box>
      )
    }

    // Render the form with submitted data OR a PDF preview if provided
    return (
      <>
        <div>
          <div className="my-4 flex items-center justify-between gap-2">
            <div>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  navigate(
                    routes.formTemplate({
                      orgName,
                      id: submission.formTemplateId,
                    })
                  )
                }
              >
                Edit Form
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {submission.submissionType !== 'PDF' && (
                <Button
                  startIcon={<PrintIcon />}
                  onClick={() =>
                    printForm(
                      submission.formTemplate,
                      submission.submissionData
                    )
                  }
                  variant="outlined"
                  size="small"
                  disabled={!submission}
                >
                  Print
                </Button>
              )}
              <Button
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
                variant="outlined"
                color="error"
                size="small"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Submission'}
              </Button>
            </div>
          </div>

          <div className="flex justify-between gap-2">
            <div>
              <Typography variant="h5" className="mb-1">
                {submission.formTemplate.title}
              </Typography>

              {submission.formTemplate.description && (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  className="mb-2"
                >
                  {submission.formTemplate.description}
                </Typography>
              )}
            </div>

            <div>
              <Typography variant="body2" color="text.secondary">
                Submitted: {new Date(submission.submittedAt).toLocaleString()}
              </Typography>

              {submission.submittingUser && (
                <Typography variant="body2" color="text.secondary">
                  By: {submission.submittingUser.firstName}{' '}
                  {submission.submittingUser.lastName}
                </Typography>
              )}
              {submission.status === 'ARCHIVED' && submission.archivedAt && (
                <Typography variant="body2" color="text.secondary">
                  Archived: {new Date(submission.archivedAt).toLocaleString()}
                </Typography>
              )}
            </div>
          </div>
        </div>

        {isPdfSubmission ? (
          <div className="rounded-lg border border-gray-200">
            {submission.submissionFile?.urlForPdfPreview ? (
              <div>
                {/* Embedded PDF viewer */}
                <div style={{ width: '100%', height: '800px' }}>
                  <iframe
                    title="selectedFile-preview"
                    src={submission.submissionFile.urlForPdfPreview}
                    className="h-full w-full"
                  />
                </div>
              </div>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Unable to load PDF preview.
              </Typography>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 px-4">
            {submission && (
              <IsolatedFormRenderer
                formSubmission={submission}
                formTemplate={submission.formTemplate}
                onSubmit={() => {}}
                onError={() => {}}
                options={{
                  readOnly: true,
                  noAlerts: true,
                }}
              />
            )}
          </div>
        )}
      </>
    )
  }

  return (
    <RootPageStyledDiv>
      <Metadata title={getPageTitle()} description="View Form Submission" />
      {renderContent()}
      <ConfirmationModal
        title="Delete Form Submission"
        message="Are you sure you want to delete this form submission? This action cannot be undone."
        confirmText="Delete"
        denyText="Cancel"
        discriminator="delete-submission"
        onConfirm={handleDeleteConfirm}
        onDeny={handleDeleteDeny}
      />
    </RootPageStyledDiv>
  )
}

export default ViewFormSubmissionPage
